import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  getEmailHistory,
  getDraftEmailHistory,
  previewDraftEmail,
  getEmailInfo,
} from "../services/adminEmail_service";
import excelIcon from "../../../assets/images/SBA/excel_icon.png";
import { exportToExcel } from "../../../shared/utils/exportToExcel";
import useDocumentTitle from "../../../shared/customHooks/useDocumentTitle";
import { AESClientWebsiteDrawer } from "../../../shared/utils/aesClientWebsiteDrawer";
const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const mm = `${d.getMonth() + 1}`.padStart(2, "0");
  const dd = `${d.getDate()}`.padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
};

const normalize = (item) => ({
  emailId: item.emailId,
  subject: item.subject,
  sendDate: item.sendDate,
  createdName: item.createdName,
  recipientsCount: item.recipientsCount,
  documentCount: item.documentCount,
  templateId: item.templateId,
  modifiedBy: item.modifiedBy,
  status: item.status,
});



export default function HistoryTab({ isActive = true }) {

  useDocumentTitle("Email History");
  const [rows, setRows] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageSize] = useState(15);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRecipients, setPreviewRecipients] = useState([]);
  const [previewDocuments, setPreviewDocuments] = useState([]);
  const [previewBody, setPreviewBody] = useState("");
  const [previewIndex, setPreviewIndex] = useState(0);

  const loadGrid = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg("");
      const response = await getEmailHistory("4");
      const raw = response?.data?.data ?? response?.data ?? response;
      const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
      setRows(list.map(normalize));
    } catch (e) {
      setRows([]);
      setErrorMsg("Failed to load email history.");
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    if (isActive) {
      loadGrid();
    }
  }, [isActive, loadGrid]);

useEffect(() => {
  const style = document.createElement("style");
  style.innerHTML = `
    .aes-drawer-header {
      background: #1e1a66 !important;
      color: #fff !important;
    }

    .aes-drawer-header .rs-drawer-title {
      font-weight: 600 !important;
      font-size: 20px !important;
      color: #fff !important;
      margin: 0 !important;
    }

    .aes-drawer-header .rs-drawer-header-close {
      color: #fff !important;
      opacity: 1 !important;
    }
  `;
  document.head.appendChild(style);
  return () => document.head.removeChild(style);
}, []);


  const columnDefs = useMemo(
    () => [
      {
        headerName: "Subject",
        minWidth: 280,
        flex: 1.4,
        sortable: true,
        filter: true,
        filter: "agTextColumnFilter",
        valueFormatter: (p) => p.data?.subject || "",
      },
      {
        headerName: "Date",
        minWidth: 140,
        sortable: true,
        filter: true,
        filter: "agTextColumnFilter",
        valueFormatter: (p) => formatDate(p.value),
      },
      {
        headerName: "Created By",
        minWidth: 180,
        flex: 1,
        sortable: true,
        filter: "agTextColumnFilter",
        valueFormatter: (p) => p.data?.createdName || "",
      },
      {
        headerName: "Sent",
        minWidth: 110,
        sortable: true,
        filter: "agNumberColumnFilter",
        valueFormatter: (p) => Number(p.data?.recipientsCount || 0),
      },
      {
        headerName: "Alerts",
        minWidth: 110,
        sortable: true,
        filter: "agNumberColumnFilter",
        valueFormatter: (p) => Number(p.data?.documentCount || 0),
      },
      {
        headerName: "Action",
        minWidth: 90,
        sortable: false,
        filter: false,
        cellRenderer: (p) => (
          <button
            type="button"
            title="Preview"
            onClick={() => openPreview(p.data)}
            style={{ border: "none", background: "transparent", cursor: "pointer" }}
          >
            <i className="fa fa-search" aria-hidden="true"></i>
          </button>
        ),
      },
    ],
    []
  );

  const defaultColDef = useMemo(() => ({ resizable: true, floatingFilter: true }), []);

  const handleExport = () => {
    const headers = ["Subject", "Date", "Created By", "Sent", "Alerts"];
    const exportRows = (rows || []).map((r) => ({
      Subject: r.subject,
      Date: formatDate(r.sendDate),
      "Created By": r.createdName,
      Sent: Number(r.recipientsCount || 0),
      Alerts: Number(r.documentCount || 0),
    }));

    exportToExcel({ fileName: "Email History.xlsx", headers, rows: exportRows });
  };

  const openPreview = async (row) => {
    try {
      const emailId = row.emailId;

      // const draftRes = await getDraftEmailHistory(emailId, row.templateId || null);
      const draftRes = await getDraftEmailHistory(emailId, null);
      const draft = draftRes?.data?.data ?? draftRes?.data ?? draftRes;

      const templateBody = draft?.Body || draft?.body || "";
      if (!templateBody) {
        setErrorMsg("Template body is empty.");
        return;
      }

      const previewRes = await previewDraftEmail(emailId, templateBody);
      const previewList = previewRes?.data?.data ?? previewRes?.data ?? previewRes;

      const infoRes = await getEmailInfo(emailId);
      const info = infoRes?.data?.data ?? infoRes?.data ?? infoRes;

      const recipients = Array.isArray(previewList) ? previewList : [];
      const normalizedRecipients = recipients.map((r) => ({
        sendName: r.sendName,
        firstName: r.firstName,
        lastName: r.lastName,
        webUserId: r.webUserId,
        mergedEmailBody: r.mergedEmailBody,
      }));

      const docList = info?.documentList ?? [];
      const documents = Array.isArray(docList)
        ? docList.map((d) => ({
            documentName: d.documentName ?? "",
            documentDate: d.documentDate ?? "",
          }))
        : [];

      setPreviewRecipients(normalizedRecipients);
      setPreviewDocuments(documents);
      setPreviewBody(normalizedRecipients[0]?.mergedEmailBody || "");
      setPreviewIndex(0);
      setPreviewOpen(true);
    } catch (e) {
      setErrorMsg("Failed to load preview.");
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewRecipients([]);
    setPreviewDocuments([]);
    setPreviewBody("");
    setPreviewIndex(0);
  };

  const nextPreview = () => {
    if (previewIndex + 1 >= previewRecipients.length) return;
    const idx = previewIndex + 1;
    setPreviewIndex(idx);
    setPreviewBody(previewRecipients[idx]?.mergedEmailBody || "");
  };

  const prevPreview = () => {
    if (previewIndex <= 0) return;
    const idx = previewIndex - 1;
    setPreviewIndex(idx);
    setPreviewBody(previewRecipients[idx]?.mergedEmailBody || "");
  };

  const currentRecipient = previewRecipients[previewIndex] || {};

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button
          type="button"
          title="Export to Excel"
          onClick={handleExport}
          style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer" }}
        >
          <img src={excelIcon} alt="Export" style={{ width: 28, height: 28 }} />
        </button>
      </div>

      {errorMsg ? <div style={{ color: "red", marginBottom: 8 }}>{errorMsg}</div> : null}

      <div className="ag-theme-alpine" style={{ width: "100%", height: 520 }}>
        <AgGridReact
          theme={"legacy"}
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination
          paginationPageSize={pageSize}
          paginationPageSizeSelector={[10, 15, 50, 100, 200]}
          suppressCellFocus
          overlayNoRowsTemplate={loading ? "Loading..." : "No Records Available"}
        />
      </div>

      <AESClientWebsiteDrawer
        open={previewOpen}
        setOpen={setPreviewOpen}
        title="Mail Content Preview"
        size="md"
        HeaderClassName="aes-drawer-header"
       
      >
        <div style={{ padding: 16 }}>

          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <div>
              <strong>{currentRecipient.firstName} {currentRecipient.lastName}</strong>, {currentRecipient.webUserId}
            </div>

            <div>
              {previewRecipients.length ? previewIndex + 1 : 0} of {previewRecipients.length} Recipients

              <span style={{ marginLeft: 10 }}>
                <a href="#" onClick={(e) => { e.preventDefault(); prevPreview(); }}>
                  &#8249;
                </a>

                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); nextPreview(); }}
                  style={{ marginLeft: 8 }}
                >
                  &#8250;
                </a>
              </span>
            </div>
          </div>

          <hr style={{ borderTop: "1px solid #2C81BA" }} />

          <div dangerouslySetInnerHTML={{ __html: previewBody }} />

          <div style={{ marginTop: 16, display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <table className="table table-bordered table-striped">
                <thead>
                  <tr>
                    <th>Recipient Name</th>
                    <th>Email</th>
                  </tr>
                </thead>

                <tbody>
                  {previewRecipients.map((r, idx) => (
                    <tr key={idx}>
                      <td>{r.sendName || `${r.firstName || ""} ${r.lastName || ""}`}</td>
                      <td>{r.webUserId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ flex: 1 }}>
              <table className="table table-bordered table-striped">
                <thead>
                  <tr>
                    <th>Document Name</th>
                    <th>Date</th>
                  </tr>
                </thead>

                <tbody>
                  {previewDocuments.map((d, idx) => (
                    <tr key={idx}>
                      <td>{d.documentName}</td>
                      <td>{formatDate(d.documentDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <button className="btn btn-default" onClick={closePreview}>
              Cancel
            </button>
          </div>

        </div>
      </AESClientWebsiteDrawer>
    </div>
  );
}
